import createSelector from 'lib/web/createSelector'
import config from 'constants/config'
import { ITEM_STATUS_COMPLETED, ITEM_TYPE_OFFER } from 'constants/cart'

import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_CRUISE, OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR } from 'constants/offer'
import { changeDatesUrlPattern, changePackageUrlPattern, checkoutPaymentUrlPattern, bookingPaymentUrlPattern } from 'constants/url'
import { isLEHotel, isVillaOffer } from 'lib/offer/offerTypes'
import {
  INSTALMENT_PLANS,
  PAYMENT_OPTIONS,
  PaymentMethod,
  RAZORPAY_PAYMENT_TYPE,
  STRIPE_PAYMENT_TYPE,
} from 'constants/payment'
import {
  getAccommodationTotal,
  getDepositTotal,
  getDiscountTotal,
  getGrandTotal,
  getMaximumCreditTotal,
  getPayInInstalmentsTotal,
  getTotalAfterDiscount,
} from './cartAmountSelectors'
import {
  isPaypalEnabled,
  isWithInStartDateRange,
  isKlarnaEnabled,
  isWithinKlarnaAvailabilityRange,
  isKlarnaBpEnabled,
  isWithinKlarnaBpAvailabilityRange,
} from 'lib/payment/paymentUtils'
import { cartIncludesFlights, isOneWayFlight } from 'lib/flights/flightUtils'
import { arrayToObject, fillArray, groupBy, sortBy, sum } from 'lib/array/arrayUtils'
import { formatDynamicAmount } from 'lib/format/formatCurrencyIntl'
import {
  getSumExtraGuestsSurcharge,
  includedGuestsExceededItems as includedGuestsExceeded,
  isReservationAvailableForRoom,
} from 'lib/checkout/cartReservationUtils'
import { RESERVATION_TYPE_BOOK_LATER, RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { cartCalendarFlightsToggled, getAvailableDayFromCalendar, getSelectedDay } from './cartCalendarSelectors'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import { isCoverGeniusEnabled, isCoverGeniusEndDateValid, providerIsCoverGenius } from 'lib/insurance/insuranceHelpers'
import { countOccupants, countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { canAddInsuranceToOrder } from 'lib/order/orderUtils'
import { isApplePayEnabled } from 'lib/payment/applePayUtils'
import { isGooglePayEnabled } from 'lib/payment/googlePayUtils'
import { isAfterPayBpEnabled, isWithinAfterPayBpAvailabilityRange } from 'lib/payment/afterPayBpUtils'
import {
  isStripe3dsEnabled,
  isStripe3dsForGiftCardsEnabled,
  isStripe3dsForStandaloneEnabled,
  isStripeBedbankRepeatCustomerCheckEnabled,
  isStripeWalletFlightsEnabled,
} from 'lib/payment/stripeUtils'
import { isHoolahBpEnabled, isWithinHoolahBpAvailabilityRange } from 'lib/payment/hoolahBpUtils'
import { isAtomeBpEnabled, isWithinAtomeBpAvailabilityRange } from 'lib/payment/atomeBpUtils'
import { isWaaveBpEnabled, isWithinWaaveBpAvailabilityRange } from 'lib/payment/waaveBpUtils'
import getPaymentMethods from 'lib/payment/getPaymentMethods'
import {
  calculateDepositBalanceDueDate,
  calculateDepositBalanceDueDays,
  isDepositAvailableOnAlwaysOnOffer,
  isDepositsEnabled,
  isDepositsEnabledForBNBL,
  isOfferWithInStartDateRange,
  isStripeWalletDepositsEnabled,
  isWithinDepositsAvailabilityRange,
} from 'lib/payment/depositsUtils'
import {
  calculateDaysBetweenDates,
  createPayInInstalmentOptions,
  isBookingWithInStartDateRange,
  isPayInInstalmentsEnabled,
  isStripeWalletInstalmentsEnabled,
} from 'lib/payment/payInInstalmentsUtils'
import { getCartOfferGeoCoords } from 'selectors/cart/cartExperienceSelectors'
import { hasAvailableProduct as hasAvailableInsuranceProduct } from '../checkoutInsuranceSelectors'
import { AMADEUS } from 'constants/provider'
import { getDefaultDepositAmountPercentage, getDepositServiceFeeConfig } from 'checkout/selectors/featureConfig/deposit'
import { getTransferItems } from 'selectors/bookingSidebarSelectors'
import { isStandaloneGiftCard } from 'checkout/selectors/view/giftCard'
import { isStandaloneLuxuryPlusSubscription } from 'checkout/selectors/view/luxuryPlusSubscription'

// getOfferCartItems returns the items with offer types from the cart
export const getOfferCartItems = createSelector(
  (cart: App.CartState) => cart.items,
  (items) => items.filter(item => item.type === ITEM_TYPE_OFFER),
)

export const getHotelCartItems = createSelector(
  (state: App.State) => getOfferCartItems(state.cart),
  (state: App.State) => state.cart.accommodation.offer,
  (items, offer) => offer.parentType === OFFER_TYPE_HOTEL ? items : [],
)

export const getOrderRegion = createSelector(
  (state: App.State) => state.cart,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => isChangePackage(state),
  (cart, currentRegionCode, isChangePackage) => {
    if (cart.bookDatesOnly || cart.isChangeDates || isChangePackage) {
      return cart.order.regionCode
    }
    return currentRegionCode
  },
)

export const isExperiencesEnabled = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (offer) => {
    if (isLEHotel(offer) || isVillaOffer(offer)) {
      return config.EXPERIENCES_ENABLED && offer.experiencesInFlow
    }

    return config.EXPERIENCES_ENABLED
  },
)

export const isCapacityValid = createSelector(
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.accommodation.offer,
  (occupancies, pkg, offer) =>
    occupancies.every(occupancy =>
      // all capacities must have at least one adult
      occupancy.adults >= 1 &&
      isReservationAvailableForRoom(occupancy, pkg.roomRate.capacities, offer),
    ),
)

export const includedGuestsExceededItems = createSelector(
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.accommodation.offer,
  (occupancies, pkg, offer) => occupancies.length > 0 && !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceeded(occupancies, pkg, offer),
)

export const hasIncompleteItems = createSelector(
  (state: App.State) => getOfferCartItems(state.cart),
  (items) => items.some((item: App.CartItem) => item.status !== ITEM_STATUS_COMPLETED),
)

export const baseShouldShowInsurance = createSelector(
  (state: App.State) => state.cart.isChangeDates,
  (state: App.State) => isChangePackage(state),
  (state: App.State) => state.cart.accommodation,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => hasAvailableInsuranceProduct(state),
  (state: App.State) => state.cart.accommodation.endDate || state.cart.bedbank.checkOut,
  (isChangeDates, isChangePackage, accommodation, currentRegionCode, hasAvailableInsuranceProduct, endDate) => {
    // if the travel insurance feature is disabled
    if (!config.ENABLE_TRAVEL_INSURANCE) {
      return false
    }

    // Cover Genius doesn't cover cruises
    if (isChangeDates || isChangePackage) {
      return false
    }

    if (accommodation.offer && accommodation.offer.holidayTypes.includes('Cruises')) {
      return false
    }

    if (!isCoverGeniusEnabled(currentRegionCode)) {
      return false
    }

    if (!hasAvailableInsuranceProduct) {
      return false
    }

    if (providerIsCoverGenius(currentRegionCode) && (endDate && !isCoverGeniusEndDateValid(endDate))) {
      // cover genius doesn't support insurance for purchases >= 366 days in the future
      return false
    }

    return true
  })

export const shouldShowInsurance = createSelector(
  (state: App.State) => baseShouldShowInsurance(state),
  (state: App.State) => state.cart.accommodation.reservationType,
  (state: App.State) => state.cart.order,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.offer.offers,
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isStandaloneExperience(state),
  (baseShouldShowInsurance, reservationType, order, currentRegionCode, offers, isStandaloneFlights, isStandaloneExperience) => {
    if (!baseShouldShowInsurance) {
      return false
    }

    if (isStandaloneFlights || isStandaloneExperience) {
      return false
    }

    const offer = order ? offers[order.items[0]?.offerId] : undefined
    if (order && !canAddInsuranceToOrder(currentRegionCode, order, offer)) {
      return false
    }

    if (!order &&
      reservationType &&
      reservationType !== RESERVATION_TYPE_INSTANT_BOOKING) {
      return false
    }

    return true
  })

export const shouldShowInsuranceWhenIsBookDatesForBNBL = createSelector(
  (state: App.State) => baseShouldShowInsurance(state),
  (state: App.State) => state.cart.bookDatesOnly,
  (baseShouldShowInsurance, bookDatesOnly) => {
    return baseShouldShowInsurance && bookDatesOnly
  },
)

export const getCartOccupancies = createSelector(
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.bedbank.rooms,
  (state: App.State) => state.cart.order?.items,
  (occupancies, bedbankRooms, orderItems) => {
    const completeOrderItems = orderItems?.filter(item => item.status === 'completed')
    if (completeOrderItems?.length) {
      if (completeOrderItems[0]?.offer?.parentType === OFFER_TYPE_TOUR) {
        return [{
          adults: completeOrderItems.length,
          children: 0,
          infants: 0,
        }]
      } else if (completeOrderItems.every(item => item.reservation)) {
        return completeOrderItems.map(item => item.reservation)
      }
    }
    return occupancies.length === 0 ? bedbankRooms.map(room => room.occupancy) : occupancies
  },
)

export const getCartParticipants = createSelector(
  getCartOccupancies,
  (occupancies) => countOccupants(occupancies),
)

export const getCartFlightParticipants = createSelector(
  getCartOccupancies,
  (occupancies) => countOccupantsForFlights(occupancies),
)

export const cartHasFlights = createSelector(
  (state: App.State) => state.cart.flight.journey,
  (journey) => {
    if (isOneWayFlight(journey.fareType)) {
      return journey.departing.flights.length > 0
    } else {
      return journey.departing.flights.length > 0 &&
        journey.returning.flights.length > 0
    }
  },
)

export const getItemsWithSurcharges = createSelector(
  (state: App.State) => getOfferCartItems(state.cart),
  (items) => items.filter(item => item.reservation?.durationSurchargeTotal),
)

export const getCartPackageAddons = createSelector(
  (state: App.State) => state.addon.addonsByPackage[state.cart.accommodation.package?.id],
  (addons) => addons || [],
)

export const canAddExperiences = createSelector(
  (state: App.State) => getOfferCartItems(state.cart),
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.accommodation.startDate,
  (state: App.State) => state.cart.accommodation.endDate,
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => getCartOfferGeoCoords(state),
  (state: App.State) => isExperiencesEnabled(state),
  (cartItems, offer, pkg, startDate, endDate, isStandalone, geoCoords, experiencesEnabled) => {
    if (!experiencesEnabled) {
      return false
    }

    // we don't need to add experiences for tours
    if (offer?.type === OFFER_TYPE_TOUR) {
      return false
    }

    if (isStandalone) {
      return true
    }

    if (experiencesEnabled && geoCoords.latitude && geoCoords.longitude) {
      // if we have experiences enabled and property location we can add addons
      return true
    }

    if (cartItems.length > 0) {
      // Cart must have selected dates
      return !!(offer && pkg && startDate && endDate)
    }
    return false
  },
)

export const isPaypalAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => state.cart.accommodation.startDate || state.cart.bedbank.checkIn,
  (cartCurrency, isChangePackage, isFullPaymentSelected, isPaymentBalance, startDate) => {
    const eligibilityRangeInDays = 7 // 7 days

    const isAvailable = isPaypalEnabled(cartCurrency) &&
      isWithInStartDateRange(startDate, eligibilityRangeInDays) &&
      isFullPaymentSelected &&
      !isPaymentBalance &&
      !isChangePackage

    return isAvailable
  },
)

// For user with credits on the checkout page,
// the grand total could be > 35 while the app
// is fetching the user's credit balance (assumed
// to be 0). This causes Klarna Checkout to be
// mounted and immediately unmounted once the user
// data returns and causes the grand total to equal 0

export const isKlarnaAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.bedbank.sessionId,
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => state.cart.isGift,
  (cartTotal, currency, bedbankItemSessionId, isStandaloneFlights, isFullPaymentSelected, isPaymentBalance, isChangePackage, isGift) => !bedbankItemSessionId &&
    isFullPaymentSelected &&
    isKlarnaEnabled(currency) &&
    isWithinKlarnaAvailabilityRange(cartTotal) &&
    !isStandaloneFlights && !isPaymentBalance &&
    !isChangePackage &&
    !isGift,
)

export const isKlarnaBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.bedbank.sessionId,
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (cartTotal, currency, bedbankItemSessionId, isStandaloneFlights, isFullPaymentSelected, isPaymentBalance, isChangePackage) => !bedbankItemSessionId && isFullPaymentSelected && isKlarnaBpEnabled(currency) && isWithinKlarnaBpAvailabilityRange(cartTotal, currency) && !isStandaloneFlights && !isPaymentBalance && !isChangePackage,
)

export const isAfterPayBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => state.cart.isGift,
  (cartTotal, bookDatesOnly, currency, isStandaloneFlights, isFullPaymentSelected, isPaymentBalance, isChangePackage, cartHasFlights, isGift) => isAfterPayBpEnabled(currency) &&
    !bookDatesOnly &&
    isFullPaymentSelected &&
    !isPaymentBalance &&
    !isChangePackage &&
    isWithinAfterPayBpAvailabilityRange(cartTotal, currency) &&
    !isStandaloneFlights &&
    !cartHasFlights &&
    !isGift,
)

export const isHoolahBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (cartTotal, bookDatesOnly, currency, isFullPaymentSelected, isPaymentBalance, isStandaloneFlights, isChangePackage) => isHoolahBpEnabled(currency) &&
    !bookDatesOnly &&
    isFullPaymentSelected &&
    !isPaymentBalance &&
    !isStandaloneFlights &&
    !isChangePackage &&
    isWithinHoolahBpAvailabilityRange(cartTotal, currency),
)

export const isAtomeBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (cartTotal, bookDatesOnly, currency, isFullPaymentSelected, isPaymentBalance, isStandaloneFlights, isChangePackage) => {
    const atomeAvailable = isAtomeBpEnabled(currency) &&
    !bookDatesOnly &&
    isFullPaymentSelected &&
    !isPaymentBalance &&
    !isStandaloneFlights &&
    !isChangePackage &&
    isWithinAtomeBpAvailabilityRange(cartTotal, currency)

    return atomeAvailable
  },
)

export const isWaaveBpAvailable = createSelector(
  (state: App.State) => getGrandTotal(state),
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isFullPaymentSelected(state),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => state.cart.isChangePackage,
  (cartTotal, bookDatesOnly, currency, isFullPaymentSelected, isPaymentBalance, isStandaloneFlights, isChangePackage) => {
    const waaveAvailable = isWaaveBpEnabled(currency) &&
    !bookDatesOnly &&
    isFullPaymentSelected &&
    !isPaymentBalance &&
    !isStandaloneFlights &&
    !isChangePackage &&
    isWithinWaaveBpAvailabilityRange(cartTotal, currency)

    return waaveAvailable
  },
)

export const isApplePayAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.config.canPayWithApplePay,
  (state: App.State) => cartIncludesFlights(state.cart),
  (state: App.State) => isDepositSelected(state),
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => isInstalmentsSelected(state),
  (currency, canPayWithApplePay, includesFlights, isDepositSelected, isChangePackage, isInstalmentsSelected) => {
    return isApplePayEnabled(currency) &&
    canPayWithApplePay &&
    (!includesFlights || isStripeWalletFlightsEnabled()) &&
    (isDepositSelected ? isStripeWalletDepositsEnabled : true) &&
    !isChangePackage &&
    (isInstalmentsSelected ? isStripeWalletInstalmentsEnabled : true)
  },
)

export const isGooglePayAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.config.canPayWithGooglePay,
  (state: App.State) => cartIncludesFlights(state.cart),
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isDepositSelected(state),
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => isInstalmentsSelected(state),
  (currency, canPayWithGooglePay, includesFlights, isStandaloneFlights, isDepositSelected, isChangePackage, isInstalmentsSelected) => {
    return isGooglePayEnabled(currency) &&
    canPayWithGooglePay &&
    (!includesFlights || isStripeWalletFlightsEnabled()) &&
    !isStandaloneFlights &&
    (isDepositSelected ? isStripeWalletDepositsEnabled : true) &&
    !isChangePackage &&
    (isInstalmentsSelected ? isStripeWalletInstalmentsEnabled : true)
  },
)

export const isRazorpayAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isPaymentBalancePayment(state.cart),
  (currency, isStandaloneFlights, isPaymentBalance) => getPaymentMethods(currency).includes(RAZORPAY_PAYMENT_TYPE) && !isStandaloneFlights && !isPaymentBalance,
)

export const isStripe3dsAvailable = createSelector(
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => isGiftCardPurchase(state.cart),
  (state: App.State) => state.auth.account.numberOfPurchases,
  (state: App.State) => state.cart.bedbank.sessionId,
  isRazorpayAvailable,
  (isStandaloneFlights, isGiftCardPurchase, numberOfPurchases, bedbankItemSessionId, razorpayAvailable) => (
    (isStandaloneFlights && isStripe3dsForStandaloneEnabled()) ||
    (isGiftCardPurchase && isStripe3dsForGiftCardsEnabled()) ||
    (numberOfPurchases < 1 && bedbankItemSessionId != null && isStripeBedbankRepeatCustomerCheckEnabled()) ||
    isStripe3dsEnabled()
  ) && !razorpayAvailable,
)

export const isStripeAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => isPaymentBalancePayment(state.cart),
  isStripe3dsAvailable,
  isRazorpayAvailable,
  (currency, isPaymentBalance, stripe3dsAvailable, razorpayAvailable) => getPaymentMethods(currency).includes(STRIPE_PAYMENT_TYPE) && !stripe3dsAvailable && !razorpayAvailable && !isPaymentBalance,
)

export const getPaymentMethodAvailability = createSelector(
  isStripeAvailable,
  isPaypalAvailable,
  isRazorpayAvailable,
  isKlarnaAvailable,
  isKlarnaBpAvailable,
  isAfterPayBpAvailable,
  isHoolahBpAvailable,
  isGooglePayAvailable,
  isApplePayAvailable,
  isStripe3dsAvailable,
  isAtomeBpAvailable,
  isWaaveBpAvailable,
  (state: App.State) => state.cart.promo,
  (state: App.State, checkPromo: boolean = true) => checkPromo,
  (
    stripeAvailable,
    paypalAvailable,
    razorpayAvailable,
    klarnaAvailable,
    klarnaBpAvailable,
    afterPayBpAvailable,
    hoolahBpAvailable,
    googlePayAvailable,
    applePayAvailable,
    stripe3dsAvailable,
    atomeBpAvailable,
    waaveBpAvailable,
    promo,
    checkPromo,
  ): Record<PaymentMethod, boolean> => {
    const paymentMethods: Record<PaymentMethod, boolean> = {
      stripe: stripeAvailable,
      braintree: paypalAvailable,
      razorpay: razorpayAvailable,
      klarna: klarnaAvailable,
      klarna_bp: klarnaBpAvailable,
      afterpay_bp: afterPayBpAvailable,
      hoolah_bp: hoolahBpAvailable,
      googlepay: googlePayAvailable,
      applepay: applePayAvailable,
      stripe_3ds: stripe3dsAvailable,
      atome_bp: atomeBpAvailable,
      waave_bp: waaveBpAvailable,
      gocardless: false, // only on new check out
      velocity: false, // Only enabled for the new checkout
      stripe_payment_element_google_pay: false, // Only enabled for the new checkout
      stripe_payment_element_apple_pay: false, // Only enabled for the new checkout
      stripe_payment_element_card: false, // Only enabled for the new checkout
      stripe_payment_element_giropay: false, // // Only enabled for the new checkout
      stripe_payment_element_payto: false, // // Only enabled for the new checkout
    }
    if (checkPromo && promo.id && promo.allowedPaymentMethods?.length > 0) {
      for (const [key, value] of Object.entries(paymentMethods)) {
        paymentMethods[key] = promo.allowedPaymentMethods.includes(key) && value
      }
    }
    return paymentMethods
  },
)

export const isAnyInstalmentPayAvailable = createSelector(
  (state: App.State) => isKlarnaAvailable(state),
  (state: App.State) => isAfterPayBpAvailable(state),
  (state: App.State) => isKlarnaBpAvailable(state),
  (state: App.State) => isHoolahBpAvailable(state),
  (state: App.State) => isAtomeBpAvailable(state),
  (klarna, afterPayBp, klarna_bp, hoolah_bp, atome_bp) => klarna || afterPayBp || klarna_bp || hoolah_bp || atome_bp,
)

const isCreditWithInRange = (cartTotal: number, creditTotal: number, depositTotal: number, useLeCredit: boolean, isSpoofed: boolean, rebookingID: string) => {
  if (isSpoofed && rebookingID) {
    return true
  }

  if (useLeCredit) {
    return creditTotal < (cartTotal - depositTotal)
  }

  return true
}

export const isDepositsAvailable = createSelector(
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getAccommodationTotal(state),
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => getDepositTotal(state),
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.accommodation,
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => state.cart.bedbank.sessionId,
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.isChangePackage,
  (state: App.State) => state.cart.order,
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => state.cart.isGift,
  (cartTotal, accommodationTotal, useLeCredit, depositTotal, currency, accommodation, creditTotal, isStandaloneFlights,
    bedbankItemSessionId, isSpoofed, rebookingID, isChangePackage, order, cartHasFlights, isGift) => {
    const depositsAvailable = !bedbankItemSessionId &&
    (cartHasFlights ? config.FLIGHTS_DEPOSITS_ENABLED : true) &&
    !accommodation.offer?.disableDeposit && // Flag to check whether Deposit is disabled from admin side or not
    !isStandaloneFlights &&
    isDepositsEnabled(currency) &&
    !order?.payments?.length &&
    (accommodation.reservationType === 'buy_now_book_later' ? isDepositsEnabledForBNBL() : true) &&
    isWithinDepositsAvailabilityRange(accommodationTotal, currency) &&
    isCreditWithInRange(cartTotal, creditTotal, depositTotal, useLeCredit, isSpoofed, rebookingID) &&
    isDepositAvailableOnAlwaysOnOffer(accommodation.offer?.type, accommodation.package) &&
    isOfferWithInStartDateRange(accommodation.startDate, accommodation.offer, accommodation.package, accommodation?.cancellationPolicies) &&
    [OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR, OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_CRUISE].includes(accommodation.offer?.type) &&
    !isGift

    return !isChangePackage && depositsAvailable
  },
)

export const isPayInInstalmentsAvailable = createSelector(
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.accommodation,
  (state: App.State) => state.cart.order,
  (state: App.State) => getTotalAfterDiscount(state),
  (state: App.State) => getMaximumCreditTotal(state),
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => getDepositTotal(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  (state: App.State) => state.cart.isGift,
  (currency, accommodation, order, cartTotal, creditTotal, useLeCredit, depositTotal, isSpoofed, rebookingID, isGift) => {
    return isPayInInstalmentsEnabled(currency) &&
    (config.PAYMENTS_ENABLE_INSTALMENTS_REBOOKING && isSpoofed) &&
    !order?.payments?.length &&
    isCreditWithInRange(cartTotal, creditTotal, depositTotal, useLeCredit, isSpoofed, rebookingID) &&
    isBookingWithInStartDateRange(accommodation.startDate, accommodation.offer) &&
    [OFFER_TYPE_HOTEL].includes(accommodation.offer?.type) &&
    !isGift &&
    accommodation.reservationType !== RESERVATION_TYPE_BOOK_LATER &&
    !accommodation.offer?.disableDeposit // Flag to check whether Deposit is disabled from admin side
  },
)

/**
 * @deprecated
 * Use: getDepositServiceFeeConfig() directly
 */
export const getServiceFee = createSelector(
  getDepositServiceFeeConfig,
  (result) => result,
)

export const getDepositBalanceDueDate = createSelector(
  (state: App.State) => state.cart.accommodation,
  (accommodation) => calculateDepositBalanceDueDate(accommodation),
)

export const getDepositBalanceDueDays = createSelector(
  (state: App.State) => state.cart.accommodation,
  (accommodation) => calculateDepositBalanceDueDays(accommodation),
)

export interface PackageOptionView {
  offer: App.Offer;
  hasFlightPrices?: boolean;
  package: App.Package;
  capacityValid?: boolean;
  isAvailable?: boolean;
  price: number;
  priceDifference: number;
  isSelected?: boolean;
  withFlights?: boolean;
  value?: number;
  flightPrice?: number;
  flightsAvailable?: boolean;
}

export const canSwitchPackages = createSelector(
  (state: App.State) => state.cart.bookDatesOnly,
  (state: App.State) => state.cart.isChangeDates,
  (bookDatesOnly, isChangeDates) => {
    if (isChangeDates && !config.DATES_AND_PACKAGE_CHANGE_ENABLED) {
      return false
    }
    return !bookDatesOnly
  },
)

export const canUpgradePackages = createSelector(
  (state: App.State) => canSwitchPackages(state),
  (canSwitch) => canSwitch,
)

export function getPackageBasePriceTotal(
  packagePrice: number,
  packageCount: number,
) {
  let price = 0; const packagesToAdd = packageCount
  if (packagesToAdd > 0) {
    // apply normal price to any remaining packages
    price += packagePrice * packagesToAdd
  }

  return price
}

function getPackagePrice(
  pkg: App.Package,
  offer: App.Offer,
  withFlights: boolean,
  occupancies: Array<App.Occupants>,
  calendar?: App.Calendar,
  originAirportCode?: string,
  checkIn?: string,
) {
  const cacheActive = calendar?.flightCacheActive ?? !offer.flightCacheDisabled
  const offerFlightPrice = offer.flightPrices[originAirportCode] ?? 0
  const useFlightPrice = withFlights && cacheActive
  let calendarDay
  if (calendar) {
    if (!checkIn) {
      // date not selected yet, but we have the latest prices from the calendar
      // grab the lowest price available and show that instead of the
      // maybe-not-accurate package price
      calendarDay = useFlightPrice ? calendar.cheapestDayWithFlights : calendar.cheapestDay
    } else {
      calendarDay = getAvailableDayFromCalendar(checkIn, calendar, useFlightPrice)
    }
  }

  const basePkgPrice = getPackageBasePriceTotal(
    pkg.price,
    occupancies.length,
  )

  const extraGuestSurcharges = getSumExtraGuestsSurcharge(pkg, occupancies, offer)

  let price = basePkgPrice + (calendarDay?.surcharge ?? 0) + extraGuestSurcharges
  if (useFlightPrice) {
    price += calendarDay?.flightPrice ?? offerFlightPrice
  }

  return price
}

export const getAllPackageOptions = createSelector(
  (state: App.State) => state.cart.accommodation.occupancies,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.calendar.calendarsByPackageKey,
  (state: App.State) => state.router.location.search,
  (state: App.State) => getSelectedDay(state),
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => cartCalendarFlightsToggled(state),
  (state: App.State) => state.cart.accommodation.startDate,
  (
    occupancies,
    offer,
    calendars,
    search,
    selectedDay,
    currentPackage,
    withFlights,
    startDate,
  ) => {
    if (offer && currentPackage && occupancies.length > 0) {
      const params = parseSearchString(search)

      const currentPackagePrice = getPackagePrice(
        currentPackage,
        offer,
        withFlights,
        occupancies,
        calendars[currentPackage.uniqueKey],
        params.originAirportCode,
        startDate,
      )

      return offer.packages.map<PackageOptionView>((pkg) => {
        const calendar = calendars[pkg.uniqueKey]
        const cacheActive = calendar?.flightCacheActive ?? !offer.flightCacheDisabled
        const calendarHasDayAvailable = calendar && startDate && !!getAvailableDayFromCalendar(startDate, calendar, withFlights)
        const price = getPackagePrice(
          pkg,
          offer,
          withFlights,
          occupancies,
          calendar,
          params.originAirportCode,
          startDate,
        )

        return {
          offer,
          package: pkg,
          withFlights,
          hasFlightPrices: cacheActive,
          isSelected: pkg.uniqueKey === currentPackage.uniqueKey,
          // can make a reservation for current setup
          capacityValid: pkg.roomType && occupancies.every(occupancy => isReservationAvailableForRoom(occupancy, pkg.roomRate.capacities, offer)),
          // assume availability if dates haven't been picked yet
          isAvailable: !selectedDay || calendarHasDayAvailable,
          price,
          priceDifference: price - currentPackagePrice,
        }
      })
    }
    return []
  },
)

export const getSwitchablePackageRoomOptions = createSelector(
  (state: App.State) => canSwitchPackages(state),
  (state: App.State) => getAllPackageOptions(state),
  (state: App.State) => state.cart.accommodation.package,
  (canSwitch, allOptions, currentPackage) => {
    if (canSwitch && currentPackage) {
      const originalOption = allOptions.find(o => o.package.uniqueKey === currentPackage.uniqueKey)
      const switchableOptions = allOptions.filter(o => o.package.duration === originalOption.package.duration)
      return sortBy(switchableOptions, option => option.package.price, 'asc')
    }
    return []
  },
)

export const getSwitchableNightPackageOptions = createSelector(
  (state: App.State) => canSwitchPackages(state),
  (state: App.State) => getAllPackageOptions(state),
  (state: App.State) => state.cart.accommodation.package,
  (canSwitch, allOptions, currentPackage) => {
    if (canSwitch && currentPackage) {
      const groupedByNight = groupBy(allOptions, o => o.package.duration)
      const switchableOptions = Array.from(groupedByNight.values()).map(options => {
        // try find the different night version of the currently selected room
        const matchingRoomOption = options.find(o => o.capacityValid && o.package.roomType.id === currentPackage.roomType.id)
        // always take the package that's selected if there is one
        // otherwise try match the room option, then the first one that's valid or just the first one if none are valid
        return options.find(o => o.isSelected) || matchingRoomOption || options.find(o => o.capacityValid) || options[0]
      })
      return sortBy(switchableOptions, option => option.package.duration, 'asc')
    }
    return []
  },
)

export const getSwitchablePackageOptions = createSelector(
  (state: App.State) => getSwitchablePackageRoomOptions(state),
  (state: App.State) => getSwitchableNightPackageOptions(state),
  (roomPackages, nightPackages) => Array.from(new Set([...roomPackages, ...nightPackages])),
)

export const getUpgradePackages = createSelector(
  (state: App.State) => canUpgradePackages(state),
  (state: App.State) => getAllPackageOptions(state),
  (state: App.State) => state.cart.accommodation.originalPackageKey,
  (canUpgrade, options, originalPackageKey) => {
    if (canUpgrade && originalPackageKey) {
      const originalOption = options.find(o => o.package.uniqueKey === originalPackageKey)
      const upgradeableOptions = options.filter(o =>
        // same number of nights
        o.package.duration === originalOption.package.duration &&
        // is more expensive than original
        o.price > originalOption.package.price,
      )

      if (upgradeableOptions.length > 0) {
        return [originalOption, ...sortBy(upgradeableOptions, option => option.package.price, 'asc')]
      }
    }
    return []
  },
)

export const hasPackagePropertyBeenUpgraded = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.accommodation.originalPackageKey,
  (state: App.State) => state.cart.accommodation.package,
  (offer, originalPackageKey, currentPackage) => {
    if (offer && originalPackageKey && currentPackage?.roomType) {
      const originalPackage = offer.packages.find(p => p.uniqueKey === originalPackageKey)
      return originalPackage?.roomType.id !== currentPackage.roomType.id && currentPackage.price > originalPackage.price
    }
    return false
  },
)

export const getCartPurchaseDescription = createSelector(
  (state: App.State) => state.cart.items,
  (state: App.State) => state.cart.bedbank,
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.cart.accommodation.package,
  (state: App.State) => state.cart.currencyCode,
  (state: App.State) => state.cart.giftCard.price,
  (state: App.State) => state.geo.currentRegionCode,
  (items, bedbankItem, offer, pkg, currencyCode, giftCardPrice, regionCode) => {
    let description = ''

    if (items.length > 0) {
      if (items[0].type == ITEM_TYPE_OFFER) {
        description = `${offer.name} - ${pkg.name}`
        if (items.length > 1) {
          description += ` (${items.length} x ${offer.saleUnit})`
        }
      }
    }

    if (bedbankItem.offer) {
      description = `${bedbankItem.offer.name} - ${bedbankItem.package.name}`

      if (bedbankItem.rooms.length > 1) {
        description += ` (${bedbankItem.rooms.length} x ${bedbankItem.offer.saleUnit})`
      }
    }

    if (giftCardPrice > 0) {
      description = formatDynamicAmount(giftCardPrice, currencyCode, regionCode) + ' Gift Card'
    }

    return description
  },
)

export const cartOfferHasFlights = createSelector(
  (state: App.State) => state.cart.accommodation.offer,
  (state: App.State) => state.geo.currentRegionHasFlights,
  (offer, regionHasFlights) => regionHasFlights && offer.offerFlightsEnabled,
)

// getCartAccommodationSurchargeDiff returns the total surcharge difference to be paid
// when changing dates.
export const getCartAccommodationSurchargeDiff = createSelector(
  (state: App.CartState) => state.items,
  (state: App.CartState) => state.order?.items,
  (cartItems, orderItems) => {
    if (!orderItems || orderItems.length === 0) {
      return 0
    }

    const orderItemsMap = arrayToObject(orderItems, (item) => item.id)

    const itemsWithReservation = cartItems.filter(i => {
      const orderItem = orderItemsMap[i.orderItemId]
      return i.reservation && orderItem?.offer?.parentType !== OFFER_TYPE_TOUR && orderItem?.reservation
    })
    return sum(itemsWithReservation, i => i.reservation.durationSurchargeTotal - orderItemsMap[i.orderItemId].reservation.surcharge)
  },
)

export const getCartAccommodationSurcharges = createSelector(
  (state: App.CartState) => state.items,
  (state: App.CartState) => state.order?.items,
  (cartItems, orderItems) => {
    if (!orderItems || orderItems.length === 0) {
      return 0
    }

    const orderItemsMap = arrayToObject(orderItems, (item) => item.id)

    const itemsWithReservation = cartItems.filter(i => {
      const orderItem = orderItemsMap[i.orderItemId]
      return i.reservation && orderItem?.offer?.parentType !== OFFER_TYPE_TOUR && orderItem?.reservation
    })
    return {
      newTotalSurcharges: sum(itemsWithReservation, i => i.reservation.durationSurchargeTotal),
      totalPaidSurcharges: sum(itemsWithReservation, i => orderItemsMap[i.orderItemId].reservation.surcharge),
    }
  },
)

export const getCurrentPackageAccommodationPrice = createSelector(
  (state: App.State) => {
    const orderItemId = parseSearchString(state.router?.location?.search)?.orderItemId
    const orderItem = state.order?.order?.items?.find(i => i.id === orderItemId)
    return orderItem?.total ?? 0
  },
  (orderAccommodationPackagePrice) => {
    return +orderAccommodationPackagePrice
  },
)

export const getCartChangePackageDiff = createSelector(
  (state: App.State) => getAccommodationTotal(state),
  (state: App.State) => getCurrentPackageAccommodationPrice(state),
  (cartAccommodationTotal, currentAccommodationPrice) => {
    return cartAccommodationTotal > 0 ? cartAccommodationTotal - currentAccommodationPrice : 0
  },
)

export const getIncompleteCartItems = createSelector(
  (state: App.State) => state.cart.items,
  (items) => items.filter(item => item.status !== ITEM_STATUS_COMPLETED),
)

export const getComplimentaryCartAddons = createSelector(
  (state: App.State) => getCartPackageAddons(state),
  (state: App.State) => state.cart.accommodation.reservationType,
  (state: App.State) => getIncompleteCartItems(state).length,
  (addons, reservationType, packageCount) => {
    if (reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
      const complimentaryAddons = addons.filter(a => a.complimentary)
      // we get a set of the complimentary addons for each package we're purchasing
      // e.g. 2 complimentary addons, 3 packages = 6 addons in total
      return fillArray(packageCount, () => [...complimentaryAddons]).flat()
    }
    return []
  },
)

export const isStandaloneFlights = createSelector(
  (cartState: App.CartState) => cartState.flight.journey.departing.journeyKey !== '',
  (cartState: App.CartState) => cartState.flight.journey.returning.journeyKey !== '',
  (cartState: App.CartState) => cartState.accommodation.offer,
  (cartState: App.CartState) => cartState.bedbank.offer,
  (cartState: App.CartState) => cartState.accommodation.package,
  // If departing & returning keys available & offer or package doesn't exist, take it as a standalone flight
  (isDepartJourney, isReturnJourney, isLeOffer, isBedbankOffer, isPackage) => (isDepartJourney || isReturnJourney) && !isPackage && !isBedbankOffer && !isLeOffer,
)

export const isStandaloneExperience = createSelector(
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => !!state.cart.accommodation.offer || !!state.cart.bedbank.offer,
  (state: App.State) => state.cart.experiences.items,
  (isStandaloneFlights, hasOffer, experiences) => {
    const experienceItems = Object.values(experiences)
    return !isStandaloneFlights && !hasOffer && sum(experienceItems.flatMap(item => item.tickets).map(item => item.count)) > 0
  },
)

export const isStandaloneTransfers = createSelector(
  (state: App.State) => isStandaloneFlights(state.cart),
  (state: App.State) => !!state.cart.accommodation.offer || !!state.cart.bedbank.offer,
  getTransferItems,
  (isStandaloneFlights, hasOffer, transfers) => {
    return !isStandaloneFlights && !hasOffer && transfers.length > 0
  },
)

export const isGiftCardPurchase = createSelector(
  (state: App.CartState) => state.giftCard.price,
  (giftCardPrice) => !!giftCardPrice,
)

export const isPaymentBalancePayment = createSelector(
  (state: App.CartState) => state.paymentBalance.price,
  (paymentBalancePrice) => !!paymentBalancePrice,
)

export const getReservationPhoneNumber = createSelector(
  (state: App.State) => state.cart.items[0]?.reservation,
  (state: App.State) => state.auth.account,
  (leReservation, account) => {
    const prefix = leReservation?.phonePrefix ?? account.phonePrefix
    const phoneNumber = leReservation?.['phone.phone'] ?? account.phone
    return `+${prefix} ${phoneNumber}`
  },
)

export const getReservationName = createSelector(
  (state: App.State) => state.cart.items[0]?.reservation,
  (state: App.State) => state.auth.account,
  (leReservation, account) => {
    return {
      firstName: leReservation?.guestFirstName ?? account.givenName,
      lastName: leReservation?.guestLastName ?? account.surname,
    }
  },
)

export const getCartOfferLocation = createSelector(
  (state: App.State) => state.cart.accommodation.offer?.location,
  (state: App.State) => state.cart.bedbank.offer?.property,
  (offerLocation, bedbankProperty) => {
    if (offerLocation) {
      return offerLocation
    }

    if (bedbankProperty) {
      return [bedbankProperty.address.city, bedbankProperty.address.stateProvinceName].filter(Boolean).join(', ')
    }

    return ''
  },
)

export const isAmadeusBooking = createSelector(
  (state: App.State) => state.cart.flight.journey.provider,
  (provider) => provider === AMADEUS,
)

export const isChangePackage = createSelector(
  (state: App.State) => state.router?.location?.pathname,
  (state: App.State) => state.router?.location?.query?.orderItemId,
  (state: App.State) => state.cart.isChangePackage,
  (pathName, orderItemId, isChangePackage) => {
    const urlMatch = ((bookingPaymentUrlPattern.test(pathName) || checkoutPaymentUrlPattern.test(pathName)) && !!orderItemId) ||
      changePackageUrlPattern.test(pathName)
    return isChangePackage && urlMatch
  },
)

export const isSingleItemOrder = createSelector(
  (state: App.State) => state.order?.order?.items,
  (items) => {
    if (items?.length) {
      return items?.filter(item => item.status !== 'cancelled').length <= 1
    }
    return true
  },
)

export const isChangeDates = createSelector(
  (state: App.State) => state.router?.location?.pathname,
  (state: App.State) => state.cart.isChangeDates,
  (pathName, isChangeDates) => {
    return isChangeDates && changeDatesUrlPattern.test(pathName)
  },
)

export const isDepositSelected = createSelector(
  (state: App.State) => state.cart.paymentSelected,
  (state: App.State) => isDepositsAvailable(state),
  (paymentSelected, depositAvailable) => {
    return depositAvailable && paymentSelected === PAYMENT_OPTIONS.DEPOSIT
  },
)

export const isFullPaymentSelected = createSelector(
  (state: App.State) => state.cart.paymentSelected,
  (paymentSelected) => { return paymentSelected === PAYMENT_OPTIONS.FULL },
)

export const isInstalmentsSelected = createSelector(
  (state: App.State) => state.cart.paymentSelected,
  (paymentSelected) => { return paymentSelected === PAYMENT_OPTIONS.INSTALMENT },
)

export const getPayInInstalmentOptions = createSelector(
  (state: App.State) => getPayInInstalmentsTotal(state),
  (state: App.State) => state.cart.accommodation,
  (state: App.State) => state.cart.credit.balance,
  (state: App.State) => state.cart.useLeCredit,
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => state.auth.account.isSpoofed,
  (state: App.State) => state.cart.rebookingID,
  getDefaultDepositAmountPercentage,
  (total, accommodation, credits, canUseLeCredit, promoDiscount, isSpoofed, rebookingID, defaultDepositAmountPercentage) => {
    const depositDueDate = calculateDepositBalanceDueDate(accommodation)
    const isRebooking = isSpoofed && rebookingID && canUseLeCredit
    return createPayInInstalmentOptions(total, accommodation.startDate, accommodation.offer, depositDueDate, credits, canUseLeCredit, promoDiscount, defaultDepositAmountPercentage, isRebooking)
  },
)

export const getDefaultInstalmentPlan = createSelector(
  (state: App.State) => state.auth.account.isSpoofed,
  (isSpoofed) => {
    return isSpoofed ? INSTALMENT_PLANS.SINGLE : INSTALMENT_PLANS.WEEKLY
  },
)

export const getSelectedInstalment = createSelector(
  getDefaultInstalmentPlan,
  (state: App.State) => getPayInInstalmentOptions(state),
  (state: App.State) => state.cart.instalmentPlan,
  (defaultInstalmentPlan, instalmentOptions, instalmentPlan) => {
    // when instalmentPlan is null return default instalment which is calculated above
    return instalmentOptions.find((plan: App.CartInstalmentPlan) => plan.id === (instalmentPlan ?? defaultInstalmentPlan))
  },
)

export const cartHasExperiences = createSelector(
  (state: App.State) => state.cart.experiences.items,
  (experiences) => {
    for (const item in experiences) {
      if (experiences[item].tickets.some(ticket => ticket.count > 0)) return true
    }
    return false
  },
)

export const cartHasNonAccommodationItems = createSelector(
  (state: App.State) => cartHasFlights(state),
  (state: App.State) => cartHasExperiences(state),
  (state: App.State) => state.cart.insurance.insuranceAccepted,
  (hasFlights, hasExperiences, hasInsurance) => {
    return hasFlights || hasExperiences || hasInsurance
  },
)

export const getFinalPaymentDueDaysLeft = createSelector(
  (state: App.State) => state.cart.accommodation.startDate,
  getSelectedInstalment,
  (checkInDate, selectedInstalment) => {
    return calculateDaysBetweenDates(checkInDate, selectedInstalment.lastInstalmentDate)
  },
)

export const isSimpleStandaloneCheckout = createSelector(
  isStandaloneGiftCard,
  isStandaloneLuxuryPlusSubscription,
  (isStandaloneGiftCard, isStandaloneLuxuryPlusSubscription): boolean => isStandaloneGiftCard || isStandaloneLuxuryPlusSubscription,
)
